import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { HomePayload } from "../../shared/types.ts";
import { DropCountdown } from "../components/DropCountdown.tsx";
import { EmptyState } from "../components/EmptyState.tsx";
import { ListingCard } from "../components/ListingCard.tsx";
import { api } from "../lib/api.ts";
import { syncClock } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { useSegments } from "../lib/segments.ts";

/** Home (R-DRP-2, 08-design.md §Home). */
export function Home() {
  const { t, locale } = useI18n();
  const { me } = useMe();
  const [data, setData] = useState<HomePayload | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      api<HomePayload>("/api/home")
        .then((d) => {
          if (!live) return;
          syncClock(d.server_ts);
          setData(d);
        })
        .catch(() => undefined);
    void load();
    const id = setInterval(load, 60_000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, []);

  const segments = useSegments(data?.live.items.map((i) => i.id) ?? []);

  return (
    <div className="page">
      {data ? <DropCountdown next={data.drop.next} queued={data.drop.queued} /> : null}
      {data && data.categories.length ? (
        <div className="chips" data-testid="category-chips">
          {data.categories.map((c) => (
            <Link key={c.slug} className="chip" to={`/s?category=${c.slug}`}>
              {locale === "vi" ? c.name_vi : c.name_en}
              {c.count ? ` · ${c.count}` : ""}
            </Link>
          ))}
        </div>
      ) : null}
      <h2 className="h2">{data?.drop.after_drop ? t("drop.this_week") : t("drop.live_now")}</h2>
      {data === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : data.live.items.length === 0 ? (
        <EmptyState
          text={t("empty.browse")}
          cta={{ label: t("browse.tab.next_drop"), to: "/s?tab=next_drop" }}
        />
      ) : (
        <>
          <div className="grid" data-testid="home-grid">
            {data.live.items.map((c) => (
              <ListingCard
                key={c.id}
                card={c}
                {...(segments[c.id] ? { segment: segments[c.id] } : {})}
              />
            ))}
          </div>
          {data.live.has_more ? (
            <p>
              <Link className="btn" to="/s">
                {t("common.more")}
              </Link>
            </p>
          ) : null}
        </>
      )}
      {!me?.can_sell ? (
        <p className="muted small" style={{ marginTop: 24 }}>
          {t("home.guest_cta")}
        </p>
      ) : null}
    </div>
  );
}
